import { ReactNode, useMemo } from "react";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import {
  IcmAnimatedMoney,
  IcmMoneyDisplay,
  splitFormattedMoney,
  IcmTabbedPieChart,
} from "@/icm/common";
import {
  buildStatementBreakdownItems,
  getJobCount,
  getTotalHours,
} from "@/icm/icm-statement-page/utils";
import type {
  EnrichedIcmStatementDetail,
  IcmPayoutBreakdownItem,
  IcmPayoutDisplayType,
} from "@/icm/types";
import {
  buildLineItemsPieChartTabs,
  formatValueByDisplayType,
} from "@/icm/utils";

interface IcmStatementSummaryCardProps {
  statement: EnrichedIcmStatementDetail;
  isSidebarOpen?: boolean;
  hideTotalPay?: boolean;
}

interface SummaryMetricItem {
  key: string;
  label: string;
  displayType: IcmPayoutDisplayType;
  value: number | string | null;
}

const StatBlock = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap="xs" className="min-w-0">
    {value}
    <Typography variant="subtitleW3" textColor="secondary">
      {label}
    </Typography>
  </Stack>
);

const SummaryPane = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => <Card className={cn("p-4 sm:p-5", className)}>{children}</Card>;

export const IcmStatementSummaryCard = ({
  statement,
  isSidebarOpen = false,
  hideTotalPay = false,
}: IcmStatementSummaryCardProps) => {
  const { jobItems, hourlyItems } = useMemo(() => {
    const lineItems = statement.line_items ?? [];

    return {
      jobItems: lineItems.filter((item) => item.job_id != null),
      hourlyItems: lineItems.filter((item) => item.job_id == null),
    };
  }, [statement.line_items]);
  const breakdownItems: IcmPayoutBreakdownItem[] = useMemo(
    () => buildStatementBreakdownItems(statement),
    [statement],
  );
  const pieChartTabs = useMemo(
    () => buildLineItemsPieChartTabs(jobItems),
    [jobItems],
  );
  const hasPayoutMix = pieChartTabs.length > 0;
  const jobCount = useMemo(() => getJobCount(jobItems), [jobItems]);
  const totalHours = useMemo(() => getTotalHours(hourlyItems), [hourlyItems]);
  const totalPayout = parseFloat(statement.total_payout);
  const showSummaryOverview = !hideTotalPay || hasPayoutMix;
  const showStandaloneTotalPayCard = !hideTotalPay;
  const showInlineTotalPay = hideTotalPay || hasPayoutMix;
  const useTwoColumnSummaryLayout =
    !isSidebarOpen && !hideTotalPay && hasPayoutMix;
  const standaloneTotalPayCardClassName = cn(
    "min-h-[220px] items-center justify-center",
    {
      "hidden [@media(min-width:900px)]:flex": hasPayoutMix,
      flex: !hasPayoutMix,
    },
  );
  const inlineTotalPayClassName = cn("text-xl font-bold", {
    "[@media(min-width:900px)]:hidden": !hideTotalPay,
  });
  const summaryItems = useMemo(() => {
    const items: SummaryMetricItem[] = [];
    const hasTotalHoursBreakdown = breakdownItems.some(
      (item) => item.key === "total_hours",
    );

    if (jobCount > 0) {
      items.push({
        key: "jobs_worked",
        label: "Jobs Worked",
        displayType: "number",
        value: jobCount,
      });
    }

    if (totalHours !== null && !hasTotalHoursBreakdown) {
      items.push({
        key: "total_hours",
        label: "Total Hours",
        displayType: "number",
        value: totalHours,
      });
    }

    breakdownItems.forEach((item) => {
      items.push({
        key: item.key,
        label: item.label,
        displayType: item.displayType,
        value: item.value,
      });
    });

    return items;
  }, [breakdownItems, jobCount, totalHours]);

  return (
    <Stack gap="xl" fullWidth className="w-full">
      {showSummaryOverview && (
        <Stack
          className={cn("grid grid-cols-1 gap-6", {
            "[@media(min-width:900px)]:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]":
              useTwoColumnSummaryLayout,
          })}
        >
          {showStandaloneTotalPayCard && (
            <SummaryPane className={standaloneTotalPayCardClassName}>
              <Stack gap="xs" align="start" className="text-left">
                <IcmAnimatedMoney
                  value={totalPayout}
                  className="text-3xl font-bold sm:text-4xl"
                  centsClassName="text-base font-normal text-muted-foreground"
                />
                <Typography variant="subtitleW3" textColor="secondary">
                  Total Pay
                </Typography>
              </Stack>
            </SummaryPane>
          )}

          {hasPayoutMix && (
            <SummaryPane className="min-w-0">
              <Stack gap="md">
                <Stack
                  direction="row"
                  justify="between"
                  align="center"
                  fullWidth
                >
                  <Typography variant="subtitleW3" textColor="secondary">
                    Payout Mix
                  </Typography>
                  {showInlineTotalPay && (
                    <IcmAnimatedMoney
                      value={totalPayout}
                      className={inlineTotalPayClassName}
                      centsClassName="text-xs font-normal text-muted-foreground"
                    />
                  )}
                </Stack>
                <Stack
                  direction="row"
                  justify="center"
                  className="xl:justify-start"
                >
                  <IcmTabbedPieChart
                    tabs={pieChartTabs}
                    className="w-full max-w-[500px]"
                  />
                </Stack>
              </Stack>
            </SummaryPane>
          )}
        </Stack>
      )}

      {summaryItems.length > 0 && (
        <Card className="w-full p-5 sm:p-6">
          <Stack className="grid grid-cols-2 gap-6">
            {summaryItems.map((item) => {
              const formattedValue = formatValueByDisplayType(
                item.value,
                item.displayType,
              );

              return (
                <Stack key={item.key} className="min-w-0">
                  <StatBlock
                    label={item.label}
                    value={
                      item.displayType === "currency" &&
                      formattedValue !== "-" ? (
                        <IcmMoneyDisplay
                          {...splitFormattedMoney(formattedValue)}
                          className="text-2xl font-bold text-tertiary-text sm:text-3xl"
                          centsClassName="pt-[2px] text-sm font-normal text-muted-foreground"
                        />
                      ) : (
                        <Typography
                          variant="titleW2"
                          textColor="tertiary"
                          className="leading-none"
                        >
                          {formattedValue}
                        </Typography>
                      )
                    }
                  />
                </Stack>
              );
            })}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
